import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';

import { ResponseStatusCodes } from '@common/types';
import insureAPIs from '@services/apis/insure';

import { HealthPolicyStatusResponse } from './types';

type State = {
  policy: HealthPolicyStatusResponse | null;
  loading: boolean;
  error: string | null;
  errorResponseStatusCode: number | null;
  migrationLoading: boolean;
  migrationProposalSubmitted: boolean;
};

type Action = {
  fetchStatus: (policyId?: string) => Promise<void>;
  migrationSubmitProposal: (policyId: string) => Promise<void>;
};

export const useHealthStatus = create<State & Action>()(
  immer(
    devtools((set) => ({
      policy: null,
      loading: false,
      error: null,
      errorResponseStatusCode: null,
      migrationLoading: false,
      migrationProposalSubmitted: false,
      fetchStatus: async (policyId?: string) => {
        try {
          if (!policyId) {
            set((state) => {
              state.loading = false;
              state.error = 'Policy ID is required.';
            });
            return;
          }

          set((state) => {
            state.loading = true;
            state.error = null;
          });
          const response = await insureAPIs.getHealthStatus(policyId);

          if (response) {
            set((state) => {
              state.policy = response.data;
              state.loading = false;
            });
          }
        } catch (err: any) {
          set((state) => {
            state.loading = false;

            if (err?.response?.status === ResponseStatusCodes.FORBIDDEN) {
              return;
            }
            state.error =
              err?.message || 'Something went wrong. Please try again later.';
            state.errorResponseStatusCode = err?.response?.status;
          });
        }
      },
      migrationSubmitProposal: async (policyId: string) => {
        set((state) => {
          state.migrationLoading = true;
        });
        try {
          const status = await insureAPIs.healthFinalSubmit(policyId);
          if (status) {
            set((state) => {
              state.migrationProposalSubmitted = true;
            });
          }
        } finally {
          set((state) => {
            state.migrationLoading = false;
          });
        }
      }
    }))
  )
);
